import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Main card wrapper with border and padding
 */

export const StyledCard = styled(Stack)(({ theme }) => ({
  flex: 1,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.customShape.borderRadiusXLarge,
  padding: `${theme.spacing(4)} ${theme.spacing(6)}`,
  gap: theme.spacing(4),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}));

/**
 * Header container - flexbox for title and badge
 */
export const StyledHeaderContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
}));

/**
 * Subtitle text styling
 */
export const StyledSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(20),
  fontWeight: 400,
  lineHeight: 1.4,
  letterSpacing: '0.03px',
  color: theme.palette.text.secondary,

  [theme.breakpoints.down('sm')]: {
    fontSize: theme.typography.pxToRem(16),
  },
}));

export const StyledDurationChartContainer = styled(Box)(() => ({
  height: '216px',
}));

/**
 * Chart container with background
 */
export const StyledChartContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.contrast,
  borderRadius: theme.customShape.borderRadiusXLarge,
  padding: theme.spacing(3),
  paddingLeft: 0,
  paddingTop: theme.spacing(9),
  position: 'relative',
  backgroundImage: `
    radial-gradient(circle, ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.15)'} 1px, transparent 1px),
    radial-gradient(circle, ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.15)'} 1px, transparent 1px),
    radial-gradient(circle, ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.15)'} 1px, transparent 1px),
    radial-gradient(circle, ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.15)'} 1px, transparent 1px)
  `,
  backgroundSize: '12px 12px',
  backgroundPosition: '0 10px, 0 43px, 0 84px, 0 118px',
  backgroundRepeat: 'repeat-x',
}));

/**
 * Container for both bars
 */
export const StyledBarsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(7), // 28px between the win and loss bars
  alignItems: 'flex-start', // Changed back to flex-start since we removed padding
  width: '100%', // Ensure full width
}));

/**
 * Win bar with green gradient
 */
interface BarProps {
  width: number;
}

export const StyledWinBar = styled(Box)<BarProps>(({ theme, width }) => ({
  width: `${width}%`,
  height: theme.spacing(2), // 8px
  borderTopRightRadius: theme.customShape.borderRadiusLarge, // 12px
  borderBottomRightRadius: theme.customShape.borderRadiusLarge, // 12px
  background:
    'linear-gradient(270deg, rgba(255, 255, 255, 0.3) 0%, rgba(13, 231, 126, 0.5) 100%), linear-gradient(90deg, rgb(13, 231, 126) 0%, rgb(13, 231, 126) 100%)',
  minWidth: width > 0 ? theme.spacing(5) : 0, // 20px
}));

/**
 * Loss bar with red gradient
 */
export const StyledLossBar = styled(Box)<BarProps>(({ theme, width }) => ({
  width: `${width}%`,
  height: theme.spacing(2), // 8px
  borderTopRightRadius: theme.customShape.borderRadiusLarge, // 12px
  borderBottomRightRadius: theme.customShape.borderRadiusLarge, // 12px
  background:
    'linear-gradient(270deg, rgba(255, 255, 255, 0.36) 0%, rgba(254, 75, 75, 0.6) 100%), linear-gradient(90deg, rgb(254, 75, 75) 0%, rgb(254, 75, 75) 100%)',
  minWidth: width > 0 ? theme.spacing(5) : 0, // 20px
}));

/**
 * Legend container (part of normal flex layout)
 * Positioned 28px below the loss bar (red bar)
 */
export const StyledLegendContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(6), // Gap from the bars above
  marginLeft: theme.spacing(3),
}));

/**
 * Individual legend item with dot and label
 */
export const StyledLegendItem = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

/**
 * Legend color dot
 */
interface LegendDotProps {
  color: 'success' | 'error';
}

export const StyledLegendDot = styled(Box)<LegendDotProps>(
  ({ theme, color }) => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor:
      color === 'success'
        ? theme.palette.success.accent
        : theme.palette.error.main,
  }),
);

/**
 * Legend text
 */
export const StyledLegendText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.secondary,
}));

/**
 * Legend duration value (bold)
 */
export const StyledLegendValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.secondary,
}));

/**
 * X-axis container with time labels (outside the dotted background)
 */
export const StyledXAxisContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(1.5), // Space between chart and labels
  paddingLeft: theme.spacing(3), // Align with chart container padding
  paddingRight: theme.spacing(3), // Align with chart container padding
}));

/**
 * Individual time label on X-axis
 */
export const StyledTimeLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '0.07px',
  color: theme.palette.text.disabled,
  whiteSpace: 'nowrap',
}));

/**
 * Divider line
 */
export const StyledDivider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.divider,
}));

/**
 * Loading state container
 */
export const StyledLoadingContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: theme.spacing(75),
}));
